import {
  ArrowCircleUp,
  ArrowCounterClockwise,
  ArrowLeft,

  ArrowsClockwise,
  ArrowsOutLineVertical,
  BracketsAngle,
  CalendarBlank,
  CaretDown,
  CaretLeft,
  CaretRight,
  CaretUp,
  ChatCircle,
  ChatCircleDots,
  CircleNotch,
  Check,
  CheckCircle,
  CheckSquare,
  ClockCounterClockwise,
  Code,
  Columns,
  Copy,
  CrownSimple,
  Cube,
  Database,
  DeviceMobile,
  DeviceTablet,
  DotsSixVertical,
  DownloadSimple,
  Envelope,
  Files,
  GearSix,
  Globe,
  House,
  Image,
  Images,
  Info,
  Key,
  LinkSimple,
  ListBullets,
  ListChecks,
  ListMagnifyingGlass,
  ListNumbers,
  Lock,
  LockSimple,
  MagnifyingGlass,
  Minus,
  Monitor,
  Moon,
  PauseCircle,
  PlayCircle,
  Plugs,
  Plus,
  Quotes,
  Rows,
  ShieldCheck,
  SignOut,
  Sparkle,
  Square,
  Sun,
  TextHOne,
  TextT,
  Trash,
  Tray,
  TreeStructure,
  Asterisk,
  EnvelopeSimple,
  EyeSlash,
  Hash,
  RadioButton,
  TextAlignLeft,
  Textbox,
  UploadSimple,
  UserGear,
  UserMinus,
  UserPlus,
  Warning,
  Wrench,
  X,
  type IconProps as PhosphorProps,
  PictureInPicture,
  SidebarSimple,
  ArrowUp,
  ArrowSquareOut,
  ArrowsOutSimple,
  DotsThree,
  Stop,
  ArrowRight,
  MapPin,
  MapTrifold,
  AppWindow,
  Storefront,
  Compass,
  PuzzlePiece,
} from '@phosphor-icons/react'
import type { ComponentType } from 'react'

export interface IconProps extends Omit<PhosphorProps, 'ref'> {
  name: string
}

/**
 * Every icon this app actually references, by name. Produced by grepping
 * every `<Icon name=...>` call site plus the dynamic ones (NAV_ITEMS' nav
 * icons, BUILT_IN_BLOCKS' block icons, the device switcher, the theme
 * toggle's Sun/Moon, BlockRow's CheckSquare/Square) — see task 32's report.
 *
 * This used to be `import * as Phosphor from '@phosphor-icons/react'` with a
 * dynamic `registry[name]` lookup, which defeats tree-shaking: Rollup cannot
 * tell which of the package's ~1,500 icons a dynamic property access might
 * need, so it kept all of them. That one import was the actual cause of the
 * 5.4MB single-chunk bundle task 32 fixed — isolating it into its own
 * `manualChunks` entry (vite.config.ts) only moved the weight, it didn't cut
 * it. Explicit named imports let the bundler drop everything unused.
 *
 * Adding a new icon anywhere in the app means adding it here too — this is
 * the one place a plain string can't be resolved automatically any more.
 */
const REGISTRY: Record<string, ComponentType<PhosphorProps>> = {
  ArrowCircleUp,
  ArrowCounterClockwise,
  ArrowLeft,

  ArrowsClockwise,
  ArrowsOutLineVertical,
  BracketsAngle,
  CalendarBlank,
  CaretDown,
  CaretLeft,
  CaretRight,
  CaretUp,
  ChatCircle,
  ChatCircleDots,
  CircleNotch,
  Check,
  CheckCircle,
  CheckSquare,
  ClockCounterClockwise,
  Code,
  Columns,
  Copy,
  CrownSimple,
  Cube,
  Database,
  DeviceMobile,
  DeviceTablet,
  DotsSixVertical,
  DownloadSimple,
  Envelope,
  Files,
  GearSix,
  Globe,
  House,
  Image,
  Images,
  Info,
  Key,
  LinkSimple,
  ListBullets,
  ListChecks,
  ListMagnifyingGlass,
  ListNumbers,
  Lock,
  LockSimple,
  MagnifyingGlass,
  Minus,
  Monitor,
  Moon,
  PauseCircle,
  PlayCircle,
  Plugs,
  Plus,
  Quotes,
  Rows,
  ShieldCheck,
  SignOut,
  Sparkle,
  Square,
  Sun,
  TextHOne,
  TextT,
  Trash,
  Tray,
  TreeStructure,
  Asterisk,
  EnvelopeSimple,
  EyeSlash,
  Hash,
  RadioButton,
  TextAlignLeft,
  Textbox,
  UploadSimple,
  UserGear,
  UserMinus,
  UserPlus,
  Warning,
  Wrench,
  X,
  ArrowRight,
  PictureInPicture,
  SidebarSimple,
  ArrowUp,
  ArrowSquareOut,
  ArrowsOutSimple,
  DotsThree,
  Stop,
  MapPin,
  MapTrifold,
  AppWindow,
  Storefront,
  Compass,
  PuzzlePiece,
}

/**
 * Phosphor by name, because resolveBlockMeta returns icon names as strings.
 * An unknown name renders the Cube fallback rather than crashing the screen —
 * the same reasoning as the block metadata resolver.
 */
export function Icon({ name, size = 16, weight = 'regular', ...rest }: IconProps) {
  const Component = REGISTRY[name] ?? Cube
  return <Component size={size} weight={weight} {...rest} />
}
